'use client';

import React, { useEffect, useState } from 'react';
import styles from './Quiz.module.css';
import { QuizProps, QuizRecommendedConcept } from '../../types/quiz';
import { QuizProvider, useQuizContext } from '../../context/QuizContext';
import { useUserContext } from '../../context/UserContext';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import LabValues from './LabValues';
import QuizHeader from './QuizHeader';
import QuizItemContainer from './QuizItemContainer';
import QuizProgress from './QuizProgress';
import QuizSummary from './QuizSummary';
import { QuizShortcutsProvider } from '../../context/QuizShortcutsContext';
import { Explanation } from './types/utils';
import { QuizHotSpotProvider } from '../../context/QuizHotSpotContext';
import JoySnackbar from './JoySnackbar';
import { useCMEContext } from '../../context/CMEContext';

import QuizStartWrapper from './QuizStartWrapper';
import QuizLoggedOutBanner from './QuizLoggedoutBanner';
import QuizItemsProvider, {
  useQuizItems,
} from '../../context/QuizItemsProvider';
import { GlobalHotKeys } from 'react-hotkeys';
import DailyPracticeXPTab from './DailyPracticeXPTab';
import QuizRelevantContent from './QuizRelevantContent';
import VideoList from '../VideoList';
import { BannerAccordion } from './BannerAccordion/BannerAccordion';

type Props = {
  showRelevantContent: boolean;
  onCloseLockoutModal: () => void;
  relevantVideos: QuizRecommendedConcept[];
  queueType?: string;
};
const Body = ({
  relevantVideos,
  queueType,
  showRelevantContent,
  onCloseLockoutModal,
}: Props) => {
  const {
    showProgress,
    showLab,
    isComplete,
    isShowExplanation,
    fullQuizSize,
    setShowLab,
  } = useQuizContext();
  const { quizCredit, handleCreditStatus } = useCMEContext();
  const { user } = useUserContext();

  useEffect(() => {
    handleCreditStatus();
  }, [quizCredit?.status, isComplete, handleCreditStatus]);

  useEffect(() => {
    if (isComplete) {
      setShowLab(false);
    }
  }, [isComplete, setShowLab]);

  return (
    <>
      <Flex
        width={['100%', '100%', '95%']}
        py={[0, 0, 24]}
        justifyContent="space-between"
        alignItems="flex-start"
        margin={[0, 0, '0 auto']}
        flexDirection={['column', 'column', 'row']}
      >
        <Flex
          flex={fullQuizSize ? [1, 1, 0] : [1, 1, 0.33]}
          mr={3}
          maxWidth={400}
        >
          {/* Quiz progress toggled via quiz header */}
          {showProgress && <QuizProgress />}
          {/* Relevant content for logged in users */}
          {!user && (
            <QuizRelevantContent
              showRelevantContent={showRelevantContent}
              relevantVideos={relevantVideos}
            />
          )}
        </Flex>
        {/* Render quiz item container for each item */}
        <Flex
          flex={fullQuizSize ? [1, 1, 1] : [1, 1, 0.6]}
          justifyContent={'center'}
          minWidth="auto"
        >
          {isComplete ? (
            <QuizSummary />
          ) : (
            <QuizItemContainer
              queueType={queueType}
              onCloseLockoutModal={onCloseLockoutModal}
            />
          )}
        </Flex>
        {/* Lab values toggled via quiz header */}
        <Flex
          flex={fullQuizSize ? [1, 1, 0] : [1, 1, 0.33]}
          mb="56px"
          sx={{ position: 'relative' }}
          height="100%"
          minWidth="auto"
        >
          {showLab && <LabValues />}
          {isShowExplanation && <Explanation />}
        </Flex>
        {!isComplete && <JoySnackbar />}
      </Flex>
    </>
  );
};

type QuizContainerProps = {
  nodeIDs: number[];
  showRelevantContent: boolean;
  relevantVideos: QuizRecommendedConcept[];
  onCloseLockoutModal: () => void;
  showQuizStart?: boolean;
  queueType?: string;
  learnName: string;
};

const QuizContainer = ({
  nodeIDs,
  showQuizStart = true,
  queueType,
  relevantVideos,
  showRelevantContent,
  onCloseLockoutModal,
  learnName,
}: QuizContainerProps) => {
  const { hotkeyMap, hotkeyHandlers } = useQuizItems();

  const { changeItem, isComplete, quiz } = useQuizContext();
  const customClasses = {
    linkClasses: 'has-dark-bg ',
    textClasses:
      ' group-[.has-dark-bg]:text-white  group-[.has-dark-bg]:group-hover:text-blue-600',
  };

  const hasAnyResponse =
    Array.isArray(quiz) && quiz.some((item) => !!item.response);

  return (
    <GlobalHotKeys
      keyMap={hotkeyMap}
      handlers={hotkeyHandlers}
      allowChanges={true}
      tabIndex={0}
    >
      <QuizLoggedOutBanner
        learnName={learnName}
        isVisible={!showRelevantContent}
      />
      {showRelevantContent && !!relevantVideos.length && (
        <BannerAccordion btnText="Related videos and concepts">
          <VideoList
            isColumnLayout={false}
            testId="logged-out-quiz-video-list"
            customClasses={customClasses}
            videos={relevantVideos}
          />
        </BannerAccordion>
      )}
      {/* Header and controls for quiz */}
      <QuizHeader onCloseLockoutModal={onCloseLockoutModal} />
      <Body
        queueType={queueType}
        showRelevantContent={showRelevantContent && !!relevantVideos.length}
        onCloseLockoutModal={onCloseLockoutModal}
        relevantVideos={relevantVideos}
      />

      {/* Quiz start modal */}

      {showQuizStart && !hasAnyResponse && (
        <QuizStartWrapper nodeIDs={nodeIDs} />
      )}
      {!isComplete && <DailyPracticeXPTab handleClick={changeItem} />}
    </GlobalHotKeys>
  );
};

/**
 * Parent component of a Quiz, containing header (settings, progress bar), quiz item, summary, etc.
 */
const Quiz = (props: QuizProps) => {
  const {
    items: allItems,
    bundleID,
    quizType,
    showQuizStart,
    quizSettings,
    isDailyPractice,
    queueType,
    relevantVideos,
    revalidateData,
    learnName,
  } = props;

  const { user } = useUserContext();
  const [showRelevantContent, setShowRelevantContent] = useState(false);

  useEffect(() => {
    const showContent = localStorage.getItem('showRelevantContent');

    if (showContent && user) {
      localStorage.removeItem('showRelevantContent');
    } else if (showContent && !user) {
      setShowRelevantContent(true);
    }
  }, []);

  const handleCloseLockoutModal = () => {
    setShowRelevantContent(true);
    localStorage.setItem('showRelevantContent', 'true');
  };

  return (
    <Box className={styles.quiz} bg="bg-0">
      <QuizProvider
        revalidateData={revalidateData}
        quiz={allItems.map((item) => ({ item }))}
        bundleID={bundleID}
        isDailyPractice={isDailyPractice}
        quizSettings={quizSettings}
        quizType={quizType}
      >
        <QuizShortcutsProvider>
          <QuizHotSpotProvider>
            <QuizItemsProvider>
              <QuizContainer
                nodeIDs={allItems.map((item) => item.nodeID)}
                showQuizStart={showQuizStart}
                queueType={queueType}
                onCloseLockoutModal={handleCloseLockoutModal}
                showRelevantContent={showRelevantContent}
                relevantVideos={relevantVideos || []}
                learnName={learnName}
              />
            </QuizItemsProvider>
          </QuizHotSpotProvider>
        </QuizShortcutsProvider>
      </QuizProvider>
    </Box>
  );
};

export default Quiz;
